import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TimeframeEnum } from '@api/models/timeframe-enum';
import { FormGroup, Validators } from '@angular/forms';
import { DashboardFilterConfig, PeopleCountFormGroup } from '@admin/statistics/statistics.model';
import { MonthTranslations } from '@models/month-translation';
import { TranslateConfigService } from '@core/translate-config.service';
import { Subject, takeUntil } from 'rxjs';
import { DateTime } from 'luxon';
import { getTimeFrameFunctionMap } from '@shared/utils/timeframe.utils';
import { mapEnumToOptions } from '@app/shared/utils/map-enum-to-options.utils';
import { TrackingTypeTranslations } from '@core/translations';
@Component({
  selector: 'flow-dashboard-filter',
  templateUrl: './dashboard-filter.component.html',
  styleUrls: ['./dashboard-filter.component.scss'],
})
export class DashboardFilterComponent implements OnInit, OnDestroy {
  @Input() form: FormGroup<PeopleCountFormGroup>;
  @Input() config: DashboardFilterConfig;
  @Output() onSubmit = new EventEmitter();

  protected readonly TimeframeEnum = TimeframeEnum;

  trackingOptions = mapEnumToOptions(TrackingTypeTranslations);

  destroy$ = new Subject<void>();

  constructor(private translateConfigService: TranslateConfigService) {}

  ngOnInit(): void {
    const formStartDate = this.form.get('start');
    const formEndDate = this.form.get('end');
    this.form
      .get('timeframe')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(timeframe => {
        if (timeframe !== TimeframeEnum.Custom) {
          const [start, end] = getTimeFrameFunctionMap()[timeframe]();
          this.form.patchValue({ start: DateTime.fromISO(start), end: DateTime.fromISO(end) });
          formStartDate.removeValidators([Validators.required]);
          formEndDate.removeValidators([Validators.required]);
        }
        if (timeframe === TimeframeEnum.PreviousWeek || timeframe === TimeframeEnum.CurrentWeek) {
          this.form.get('rangeCompare').reset();
        }
        if (timeframe === TimeframeEnum.Custom) {
          formStartDate.setValue(DateTime.now().startOf('month'));
          formEndDate.setValue(DateTime.now().endOf('month'));
        }

        formStartDate.updateValueAndValidity();
        formEndDate.updateValueAndValidity();
        this.form.updateValueAndValidity();
      });

    this.form
      .get('start')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        formStartDate.setValidators(Validators.required);
        this.form.updateValueAndValidity();
      });
    this.form
      .get('end')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        formEndDate.setValidators(Validators.required);
        this.form.updateValueAndValidity();
      });
  }

  translateMonth(month: string) {
    if (month?.length > 0) return `${this.translateConfigService.instant(MonthTranslations[month])}`;
    else return '';
  }

  clearDatePickerTo() {
    this.form.get('end').setValue(null);
  }

  clearDatePickerFrom() {
    this.form.get('start').setValue(null);
  }

  clearChannels(form: FormGroup) {
    form.get('channels').setValue([]);
  }

  selectAllChannels(form: FormGroup) {
    form.get('channels').setValue(this.config.channels);
  }

  get isFormInvalid() {
    return this.form.invalid;
  }

  submit() {
    this.onSubmit.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
